import React from 'react';
import { Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';

/**
 * Twitter/X and TikTok aren't in lucide-react by default — inline SVGs so we
 * don't pull in a second icon library.
 */
export const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V7.83a8.16 8.16 0 004.77 1.52V5.89a4.85 4.85 0 01-1.84-.2z" />
  </svg>
);

/**
 * Central directory of Brian's official social outlets. Consumed by:
 *   - Footer.jsx           (small icon row in the brand column)
 *   - FollowUsBanner.jsx   (big homepage tile above the footer)
 *   - Homepage JSON-LD sameAs (Google Knowledge Graph — kept in sync manually
 *     in /app/frontend/public/index.html, /app/frontend/src/components/SEO.jsx
 *     and /app/backend/server.py)
 *
 * `brand` colour is the official brand palette hex used for the hover state
 * in FollowUsBanner.jsx. `handle` is the human-friendly display handle.
 */
export const SOCIAL_LINKS = [
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/paisleyorguk',
    handle: '@paisleyorguk',
    Icon: Facebook,
    brand: '#1877F2',
  },
  {
    name: 'X',
    url: 'https://twitter.com/paisleyorguk',
    handle: '@paisleyorguk',
    Icon: XIcon,
    brand: '#000000',
  },
  {
    name: 'Instagram',
    url: 'https://instagram.com/paisleyscotland',
    handle: '@paisleyscotland',
    Icon: Instagram,
    brand: '#E4405F',
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@paisleyscotland',
    handle: '@paisleyscotland',
    Icon: TikTokIcon,
    brand: '#010101',
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@paisleyscotland',
    handle: '@paisleyscotland',
    Icon: Youtube,
    brand: '#FF0000',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/paisleyontheweb',
    handle: 'Paisley.org.uk',
    Icon: Linkedin,
    brand: '#0A66C2',
  },
];


/**
 * Fire a GA4 event when a reader clicks one of our social outlets. Lets us
 * see in Google Analytics which platform is actually pulling clicks off the
 * site (Reports → Engagement → Events → `social_click`).
 *
 * `location` distinguishes the origin (e.g. "footer" vs "home-banner") so we
 * can compare where readers convert best.
 *
 * Safe on SSR / no-GA environments — silent no-op if `window.gtag` is missing.
 */
export const trackSocialClick = (platform, location = 'footer') => {
  if (typeof window === 'undefined') return;
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return;
  try {
    gtag('event', 'social_click', {
      social_platform: platform,   // "Facebook", "X", "Instagram", ...
      social_location: location,   // "footer" | "home-banner"
      transport_type: 'beacon',    // survives the target="_blank" navigation
    });
  } catch (_e) {
    /* ignore — analytics must never break UX */
  }
};

